import { isExplicitDesiredDate } from 'src/stores/_infrastructure/desiredDateFilter';
import { useFilterStore } from 'src/stores/filter';
import { useGenericActivityListStore } from 'src/stores/genericActivityList';
import { usePhoneCallListStore } from 'src/stores/phoneCallList';
import { RuntimeStoreContext } from 'src/types/runtime/piniaRuntimeContext';

import { DateRangeSelection } from 'src/models/common';
import { ActivityListTypeEnum, MODULE_LIST_TYPE } from 'src/models/genericActivityList';
import { PreconfiguredTimeRanges } from 'src/models/miniCalendar';
import { StatusEnum } from 'src/models/status/enums';

import { today } from 'src/util/date';
import { registerListInstanceFetcher, resolveListInstance } from 'src/util/listInstance/listInstanceFetchDispatch';
import { registerModuleTabSettingsReset } from 'src/util/listInstance/moduleTabSettingsReset';
import { QueryStore } from 'src/util/query/QueryStore';

const warmupPhoneCallList = (
	_store: RuntimeStoreContext,
	queryStore: QueryStore,
	queryParameters: {
		activityListType: ActivityListTypeEnum;
		desiredDate: DateRangeSelection | string;
		hiddenSearchTerm: string;
		searchTerm: string;
	}
) => {
	const phoneCallListStore = usePhoneCallListStore();

	void phoneCallListStore.fetchFilterBoxes({
		queryStore: queryStore,
	});
	void phoneCallListStore.fetchFilterUsers({
		queryStore: queryStore,
	});

	void fetchPhoneCallList(_store, queryStore, {
		...queryParameters,
		clearResultsBeforeFetch: false,
	});
};

const fetchPhoneCallList = (
	_store: RuntimeStoreContext,
	queryStore: QueryStore,
	queryParameters: {
		activityListType: ActivityListTypeEnum;
		clearResultsBeforeFetch: boolean;
		desiredDate: DateRangeSelection | string;
		hiddenSearchTerm: string;
		searchTerm: string;
	},
	listStore: ReturnType<typeof usePhoneCallListStore> = usePhoneCallListStore()
) => {
	const hasActiveSearch = queryParameters.searchTerm.trim() !== '' || queryParameters.hiddenSearchTerm.trim() !== '';
	const didClearSubscriptionsForSearch = hasActiveSearch;

	if (isExplicitDesiredDate(queryParameters.desiredDate)) {
		listStore.setDateFilterAction({
			dateFilter: queryParameters.desiredDate,
		});
	}

	if (hasActiveSearch) {
		listStore.clearSubscriptions({
			queryStore: queryStore,
		});
	}

	if (queryParameters.clearResultsBeforeFetch) {
		if (
			queryParameters.activityListType === ActivityListTypeEnum.OPEN ||
			queryParameters.activityListType === ActivityListTypeEnum.ALL_MODULE_LISTS_AT_ONCE
		) {
			if (queryParameters.activityListType === ActivityListTypeEnum.OPEN && !didClearSubscriptionsForSearch) {
				listStore.clearSubscriptions({
					queryStore: queryStore,
				});
			}
			listStore.silentSetOpenPhoneCallList();
		}

		if (
			queryParameters.activityListType === ActivityListTypeEnum.MINE ||
			queryParameters.activityListType === ActivityListTypeEnum.ALL_MODULE_LISTS_AT_ONCE
		) {
			if (queryParameters.activityListType === ActivityListTypeEnum.MINE && !didClearSubscriptionsForSearch) {
				listStore.clearSubscriptions({
					queryStore: queryStore,
				});
			}
			listStore.silentSetMinePhoneCallList();
		}

		if (
			queryParameters.activityListType === ActivityListTypeEnum.ALL ||
			queryParameters.activityListType === ActivityListTypeEnum.ALL_MODULE_LISTS_AT_ONCE
		) {
			if (queryParameters.activityListType === ActivityListTypeEnum.ALL && !didClearSubscriptionsForSearch) {
				listStore.clearSubscriptions({
					queryStore: queryStore,
				});
			}
			listStore.silentSetAllPhoneCallList();
		}
	}

	if (
		queryParameters.activityListType === ActivityListTypeEnum.OPEN ||
		queryParameters.activityListType === ActivityListTypeEnum.ALL_MODULE_LISTS_AT_ONCE
	) {
		if (queryParameters.activityListType === ActivityListTypeEnum.OPEN && !didClearSubscriptionsForSearch) {
			listStore.clearSubscriptions({
				queryStore: queryStore,
			});
		}
		void listStore.fetchOpenPhoneCallList({
			hiddenSearchTerm: queryParameters.hiddenSearchTerm,
			queryStore: queryStore,
			searchTerm: queryParameters.searchTerm,
		});
	}

	if (
		queryParameters.activityListType === ActivityListTypeEnum.MINE ||
		queryParameters.activityListType === ActivityListTypeEnum.ALL_MODULE_LISTS_AT_ONCE
	) {
		if (queryParameters.activityListType === ActivityListTypeEnum.MINE && !didClearSubscriptionsForSearch) {
			listStore.clearSubscriptions({
				queryStore: queryStore,
			});
		}
		void listStore.fetchMinePhoneCallList({
			hiddenSearchTerm: queryParameters.hiddenSearchTerm,
			queryStore: queryStore,
			searchTerm: queryParameters.searchTerm,
		});
	}

	if (
		queryParameters.activityListType === ActivityListTypeEnum.ALL ||
		queryParameters.activityListType === ActivityListTypeEnum.ALL_MODULE_LISTS_AT_ONCE
	) {
		if (queryParameters.activityListType === ActivityListTypeEnum.ALL && !didClearSubscriptionsForSearch) {
			listStore.clearSubscriptions({
				queryStore: queryStore,
			});
		}
		void listStore.fetchAllPhoneCallList({
			hiddenSearchTerm: queryParameters.hiddenSearchTerm,
			queryStore: queryStore,
			searchTerm: queryParameters.searchTerm,
		});
	}
};

registerListInstanceFetcher('phoneCallList', ({ listStore, params, queryStore, runtimeStore }) =>
	fetchPhoneCallList(runtimeStore as never, queryStore as never, params as never, listStore as never)
);

// Task 2.5 (analog Task 2.3 / messageList): Basis-Identifier der drei Sub-Listen-Scroll-Positionen
// (Offen/Meine/Alle) — muss `PhoneCallList.vue`s `virtual-list-id="moduleActivityListScrollerPhoneCalls"`
// spiegeln (dort mit Open/Mine/All-Suffix versehen, s. GenericActivityListTabPanel.vue). Kein
// zentraler Export vorhanden, daher hier dieselbe Literal-Konstante wie in PhoneCallList.vue.
const PHONE_CALL_LIST_SCROLL_BASE_IDENTIFIER = 'moduleActivityListScrollerPhoneCalls';

// Nur die Default-Instanz (Modul-Tabs laufen immer auf ihr) — `?list=`-Instanz-Tabs disposen
// bereits vollständig über useListInstanceLifecycle und laufen nie durch diesen Reset (Task 1.7 im
// Layout-Hook filtert sie über tab.query.list aus). Feldliste 1:1 aus clearAllFilters (FilterComponentActions.ts)
// für 'phoneCallList' abgeleitet, OHNE deren router.replace/refreshTheList: Box-/Benutzer-Filter, Datumsfilter
// (+ Zeitraum-Preset), Richtungsfilter (phoneCallList IST ein isDirectionRoute()-Ziel, analog messageList/mailList,
// anders als taskList/noteList), gewählte Shared-Query, Such-/Hidden-Suchbegriff, Mehrfachauswahl, Scroll-Positionen
// der drei Sub-Listen, aktive Ansicht.
// NICHT angefasst: sort/listViewModeLarge (persistierte Präferenzen), Datenlisten + Counts + Pagination
// + queryStoreSubscriptions (Menü-Badges/Warmup lesen den Default-Store weiter — kein $reset/$dispose).
const resetPhoneCallListModuleTabSettings = (): void => {
	const phoneCallListStore = usePhoneCallListStore();
	const filterStore = useFilterStore();
	const genericActivityListStore = useGenericActivityListStore();

	phoneCallListStore.setFilteredBoxesAction({ boxIdFilter: [] });
	phoneCallListStore.setFilteredUsersAction({ filterUsers: [] });
	phoneCallListStore.setDateFilterAction({ dateFilter: today() });
	phoneCallListStore.setDateTimeRangeFilter({ dateTimeRangeFilter: PreconfiguredTimeRanges.TODAY });
	phoneCallListStore.setDirectionFilterAction({ directionFilter: 'combined' });
	phoneCallListStore.setSelectedSharedQueryAction({ selectedSharedQuery: null });

	filterStore.setSearchTerm({ module: 'phoneCallList', searchTerm: '' });
	filterStore.setHiddenSearchTerm({ hiddenSearchTerm: '', module: 'phoneCallList' });

	genericActivityListStore.clearTheSelection({ genericListIdentifier: 'phoneCallList' });
	genericActivityListStore.resetScrollPositions(PHONE_CALL_LIST_SCROLL_BASE_IDENTIFIER);
	genericActivityListStore.setActiveView({
		activeView: MODULE_LIST_TYPE.OPEN,
		genericListIdentifier: 'phoneCallList',
	});
};

registerModuleTabSettingsReset('phoneCallList', resetPhoneCallListModuleTabSettings);

const setTheStateForTheSelectedPhoneCallAggregateIds = (
	_store: RuntimeStoreContext,
	_queryStore: QueryStore,
	queryParameters: {
		aggregateIds: string[];
		genericListIdentifier?: string;
		state: StatusEnum;
	}
) => {
	const genericActivityListStore = useGenericActivityListStore();
	const resolved = resolveListInstance(queryParameters.genericListIdentifier);

	// 'missing' (Instanz-Key ohne Registry-Eintrag = Tab bereits geschlossen) → NICHT den Default-Store
	// mutieren; 'instance' → Store DIESER Listen-Instanz; 'default' → Default-Store (byte-identisch zu vorher).
	if (resolved.outcome !== 'missing') {
		const phoneCallListStore: ReturnType<typeof usePhoneCallListStore> =
			resolved.outcome === 'instance' ? (resolved.listStore as never) : usePhoneCallListStore();

		switch (queryParameters.state) {
			case StatusEnum.UNSEEN:
				phoneCallListStore.setThePhoneCallStateToUnseen({
					aggregateIds: queryParameters.aggregateIds,
				});
				break;
			case StatusEnum.SEEN:
				phoneCallListStore.setThePhoneCallStateToSeen({
					aggregateIds: queryParameters.aggregateIds,
				});
				break;
			case StatusEnum.READY:
				phoneCallListStore.setThePhoneCallStateToReady({
					aggregateIds: queryParameters.aggregateIds,
				});
				break;
			default:
				break;
		}
	}

	void genericActivityListStore.clearTheSelection({
		genericListIdentifier: queryParameters['genericListIdentifier'] ?? 'phoneCallList',
	});
};

export { fetchPhoneCallList, setTheStateForTheSelectedPhoneCallAggregateIds, warmupPhoneCallList };
